import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaVideo, FaRedo, FaLink, FaPlay, FaSyncAlt, FaUsers, FaUserSlash, FaDesktop } from "react-icons/fa";
import StatusBadge from "../../../components/StatusBadge";
import AdminModal from "../../../components/AdminModal";
import FormField from "../../../components/FormField";
import DataTable from "../../../components/DataTable";
import KPICard from "../../../components/KPICard";
import { useToast } from "../../../components/Toast";
import { colors, spacing, typography, radius, cardBase, inputBase, btnPrimary, btnSecondary } from "../../../utils/designTokens";
import {
  sala as pedirSala,
  iniciarAnfitrion,
  accesoSala,
  reintentarSala,
  pasarAManual,
  mensajeDeError,
  detallesDeError,
  ventanaAnfitrion,
  enVentana,
  horaMontevideo,
  diaMesMontevideo,
  fechaMontevideo,
  msHastaProximo,
  urlNavegable,
} from "../../../utils/asambleas/api";

// Sección "Sala" del detalle de una asamblea con Zoom automático (Fase 3).
//
// - Estado de la sala (video.estado) con Reintentar / Pasar a link manual si falló.
//   El texto del error sale del código y de `sala` (textoErrorSala): la
//   superposición de la licencia, el cambio de horario o los accesos que Zoom
//   rechazó o que siguen fallando (sala.reprogramacion / sala.accesos), la
//   reunión que ya no existe en Zoom ("sala_inexistente": Reintentar crea otra)
//   o la sala que no se pudo crear. Con la sala pendiente porque el edificio
//   llegó al tope diario de salas de Zoom (ultimoError "limite_zoom_edificio")
//   se dice cuándo se crea y se ofrece pasar a link manual.
// - "Iniciar como anfitrión" de T-60 al cierre de la sala (fin + 60, o más con
//   una 2ª convocatoria larga; manda la ventana que devuelve `sala`): pide un
//   start_url FRESCO (iniciarAnfitrion) y lo abre en una pestaña nueva. No se
//   guarda en ningún lado (ni estado de React, ni storage): la pestaña se abre
//   en el click y la URL solo se usa para navegarla. Con la sala creada, un
//   error (superposición, reprogramación o accesos) no la apaga: el servidor
//   deja iniciarla igual, así que el botón sigue. El acceso de anfitrión es del
//   usuario de Zoom de Habitia (lo comparten todos los edificios): se pide
//   abrirlo en una ventana privada y cerrarla al terminar.
// - En una mixta, "Abrir el acceso de la sala": el link de la computadora del
//   salón ("Sala · Edificio"), que se pide aparte (accesoSala, con su propia
//   auditoría): no llama a Zoom ni trae el start_url del anfitrión, así el
//   acceso de anfitrión no llega a la notebook del salón. Se pide en el click
//   y no se guarda, igual que el del anfitrión. La computadora del salón no
//   hace esperar el cierre: si queda conectada sola, la reunión se termina al
//   cierre de la sala. Por eso se pide mantener el anfitrión hasta el final y,
//   desde 15 minutos antes del cierre, se muestra el aviso del servidor si
//   quedó sola (sala.avisoCierre).
// - Los errores de los callables con motivo conocido (tope del edificio,
//   ventana, sala cerrada…) se explican con textoMotivoZoom; si no, el mensaje
//   del servidor.
// - Vista en vivo (vivo): estado, grabación, conectados N de M, participantes
//   por unidad y los que no se pudieron identificar. Se consulta `sala` cada
//   15 s SOLO mientras la reunión está en curso y la pestaña está visible; al
//   ocultarla o salir del detalle se corta. Al volver a la pestaña (por ejemplo
//   después de abrir Zoom) se actualiza una vez.
//
// La respuesta de `sala` no trae links ni emails: solo estados y nombres.
//
// Props:
//   asamblea  vista de la asamblea ({ id, inicioMs, duracionMin, minutosSegunda, modalidad, estado, video, ... })
//   onCambio  () => void   recarga el detalle (p. ej. después de pasar a link manual)

const POLL_MS = 15000;
const MAX_TIMEOUT_MS = 2147483647;

export const ESTADOS_SALA = {
  pendiente: { label: "Pendiente", badge: "pendiente" },
  creando: { label: "Creando la sala", badge: "en_proceso" },
  lista: { label: "Lista", badge: "completada" },
  error: { label: "Con error", badge: "error" },
  finalizada: { label: "Finalizada", badge: "finalizada" },
  borrada: { label: "Borrada", badge: "cerrada" },
};

const ESTADOS_VIVO = {
  esperando: { label: "Todavía no empezó", badge: "pendiente" },
  en_curso: { label: "En curso", badge: "en_curso" },
  finalizada: { label: "Terminó", badge: "finalizada" },
};

const GRABACION = {
  no_iniciada: { label: "Sin grabar", badge: "pendiente" },
  grabando: { label: "Grabando", badge: "aprobada" },
  pausada: { label: "Grabación en pausa", badge: "pausado" },
  detenida: { label: "Grabación detenida", badge: "error" },
  completa: { label: "Grabación completa", badge: "completada" },
};

const ERRORES_SALA = {
  superposicion:
    "Hay otra reunión de Zoom en la misma franja en la cuenta de Habitia y la licencia admite una por vez. Cambiá el horario o pasá a link manual.",
  // La reunión se borró en Zoom (a mano, desde la cuenta): sus accesos ya no sirven.
  sala_inexistente:
    "La reunión ya no existe en Zoom: Reintentar crea una nueva y da de alta los accesos personales de nuevo. También podés pasar a link manual.",
  zoom_no_disponible: "Zoom automático no está disponible ahora: pasá la asamblea a link manual.",
};

// Lo que le falla a una sala ya creada (sala.reprogramacion y sala.accesos):
// "rechazo" si Zoom lo rechazó (no se vuelve a mandar igual); si no, Zoom no
// responde y el cron sigue probando.
const FALLOS_SALA = {
  reprogramacion: {
    rechazo: "Zoom rechazó el cambio de horario: la reunión sigue con el horario anterior. Reintentá o pasá a link manual.",
    pedido: "al cambio de horario",
  },
  accesos: {
    rechazo:
      "Zoom rechazó los accesos personales: los propietarios que todavía no tienen el suyo no van a poder entrar. Reintentá o pasá a link manual.",
    pedido: "al alta de los accesos personales",
  },
};

const esObjeto = (v) => !!v && typeof v === "object" && !Array.isArray(v);
const msValido = (v) => typeof v === "number" && Number.isFinite(v) && v > 0;

/** Texto de una falla de la sala creada ({codigo, rechazada, intentos, proximoIntentoMs}). */
function textoFalloSala(tipo, f) {
  const t = FALLOS_SALA[tipo];
  if (f.rechazada === true) return t.rechazo;
  const n = Math.max(0, Math.floor(Number(f.intentos) || 0));
  const veces = n > 0 ? ` (${n} ${n === 1 ? "intento" : "intentos"})` : "";
  const proximo = msValido(f.proximoIntentoMs) ? `; el próximo intento es a las ${horaMontevideo(f.proximoIntentoMs)}` : "";
  return `Zoom no responde ${t.pedido}${veces}: seguimos probando${proximo}.`;
}

/**
 * La falla que explica el error de una sala creada: la del mismo código y, si
 * ninguna coincide, la reprogramación antes que los accesos (la prioridad del
 * servidor, modelo.videoSalaCreada).
 */
function falloDeLaSala(codigo, s) {
  const rep = esObjeto(s.reprogramacion) ? s.reprogramacion : null;
  const acc = esObjeto(s.accesos) ? s.accesos : null;
  if (rep && codigo && rep.codigo === codigo) return ["reprogramacion", rep];
  if (acc && codigo && acc.codigo === codigo) return ["accesos", acc];
  if (rep) return ["reprogramacion", rep];
  if (acc) return ["accesos", acc];
  return null;
}

/**
 * Mensaje legible del error de la sala (video.error) con lo que dice `sala`
 * (la respuesta de la op `sala`): con la sala creada, la falla de la
 * reprogramación o de los accesos; sin sala, que no se pudo crear.
 */
export function textoErrorSala(codigo, sala = null) {
  const s = esObjeto(sala) ? sala : {};
  if (typeof codigo === "string" && Object.prototype.hasOwnProperty.call(ERRORES_SALA, codigo)) return ERRORES_SALA[codigo];
  if (s.creada === true) {
    const fallo = falloDeLaSala(codigo, s);
    if (fallo) return textoFalloSala(fallo[0], fallo[1]);
    return codigo
      ? `La sala de Zoom tiene un error (código: ${codigo}). Reintentá o pasá a link manual.`
      : "La sala de Zoom tiene un error. Reintentá o pasá a link manual.";
  }
  if (!codigo) return "No se pudo crear la sala de Zoom.";
  return `No se pudo crear la sala de Zoom (código: ${codigo}).`;
}

/** Cuándo se libera el cupo diario de salas de Zoom del edificio: a las 00:00 UTC. */
function proximoDiaUtcMs(ms) {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1);
}

/** "después de las 21:00 (hora de Montevideo)", o "el 21/10 después de las 21:00 (…)" si no es hoy. */
function cuandoSeLiberaElCupo(ahoraMs) {
  const libera = proximoDiaUtcMs(ahoraMs);
  const hora = `después de las ${horaMontevideo(libera)} (hora de Montevideo)`;
  return fechaMontevideo(libera) === fechaMontevideo(ahoraMs) ? hora : `el ${diaMesMontevideo(libera)} ${hora}`;
}

/**
 * Tope diario de altas y cambios de salas de Zoom del edificio
 * ("limite_zoom_edificio"; se libera a las 00:00 UTC):
 *   "crear"        la sala está pendiente y el cron la crea cuando se libera;
 *   "reintentar"   Reintentar no pudo crearla ahora (hay que volver a probar);
 *   "reprogramar"  el cambio de horario o de duración se rechazó.
 */
export function textoTopeEdificio(accion, ahoraMs = Date.now()) {
  const cuando = cuandoSeLiberaElCupo(ahoraMs);
  if (accion === "crear") {
    return `El edificio llegó al tope diario de salas de Zoom: la sala se crea ${cuando}. Si la asamblea es antes, pasala a link manual.`;
  }
  if (accion === "reprogramar") {
    return `El edificio llegó al tope diario de cambios de salas de Zoom: este cambio se puede hacer ${cuando}. Si no puede esperar, pasá la asamblea a link manual o a presencial.`;
  }
  return `El edificio llegó al tope diario de salas de Zoom: probá de nuevo ${cuando} o pasá la asamblea a link manual.`;
}

// Textos del staff por motivo y por acción (la misma clave puede querer decir
// otra cosa en otra op: "tarde" en reintentarSala es que la asamblea ya pasó).
const MOTIVOS_STAFF = {
  reintentar: {
    limite_zoom_edificio: (d, ahoraMs) => textoTopeEdificio("reintentar", ahoraMs),
  },
  reprogramar: {
    limite_zoom_edificio: (d, ahoraMs) => textoTopeEdificio("reprogramar", ahoraMs),
    sala_finalizada: () =>
      "La sala de Zoom de esta asamblea ya cerró: no se puede cambiar la fecha, la hora, la duración, el orden del día ni la videollamada (el lugar sí). Para otra fecha, creá una asamblea nueva.",
  },
  cancelar: {
    sala_finalizada: () => "La asamblea ya se hizo: no se puede suspender. Si no hubo quórum, dejalo constar en el acta.",
  },
  anfitrion: {
    temprano: (d) =>
      msValido(Number(d.habilitaEnMs))
        ? `Se puede iniciar como anfitrión desde una hora antes, a las ${horaMontevideo(Number(d.habilitaEnMs))}.`
        : "Se puede iniciar como anfitrión desde una hora antes de la asamblea.",
    tarde: () => "La ventana para iniciar como anfitrión ya terminó: la sala de Zoom cerró.",
  },
  sala: {
    temprano: (d) =>
      msValido(Number(d.habilitaEnMs))
        ? `El acceso de la sala se habilita una hora antes, a las ${horaMontevideo(Number(d.habilitaEnMs))}.`
        : "El acceso de la sala se habilita una hora antes de la asamblea.",
    tarde: () => "La ventana para abrir el acceso de la sala ya terminó: la sala de Zoom cerró.",
    no_mixta: () => "El acceso de la sala es solo para las asambleas mixtas (presencial y por videollamada).",
    sin_acceso: () =>
      "La computadora de la sala todavía no tiene su acceso: se da de alta junto con los accesos personales. Probá de nuevo en unos minutos.",
  },
};

/**
 * Texto para el staff de un error de los callables de la sala o de la
 * asamblea con Zoom, según la acción y el motivo que mandó el servidor (si no
 * lo conoce, el mensaje del servidor):
 *   reintentar   limite_zoom_edificio (cuándo se libera el cupo);
 *   reprogramar  limite_zoom_edificio, sala_finalizada (lo que toca la sala, con la sala ya cerrada);
 *   cancelar     sala_finalizada (suspender una asamblea que ya se hizo);
 *   anfitrion    temprano / tarde (fuera de la ventana: reloj adelantado o atrasado);
 *   sala         temprano / tarde, no_mixta y sin_acceso (accesoSala).
 * @param {"reintentar"|"reprogramar"|"cancelar"|"anfitrion"|"sala"} accion
 */
export function textoMotivoZoom(e, accion, ahoraMs = Date.now()) {
  const d = detallesDeError(e);
  const porMotivo = Object.prototype.hasOwnProperty.call(MOTIVOS_STAFF, accion) ? MOTIVOS_STAFF[accion] : null;
  const texto = porMotivo && typeof d.motivo === "string" && Object.prototype.hasOwnProperty.call(porMotivo, d.motivo) ? porMotivo[d.motivo] : null;
  return texto ? texto(d, ahoraMs) : mensajeDeError(e);
}

const cuenta = (v) => (Array.isArray(v) ? v.length : Number.isFinite(Number(v)) ? Number(v) : 0);

/** Etiqueta de las unidades de un participante ("502", "Apto 502, Local 1"). */
function unidadesDe(p) {
  const lista = Array.isArray(p.unidades) ? p.unidades : p.unidad != null ? [p.unidad] : [];
  return lista
    .map((u) => (u && typeof u === "object" ? u.etiqueta || u.id : u))
    .filter((u) => u != null && u !== "")
    .join(", ");
}

/**
 * Participantes del vivo (mapa por clave o lista) → { identificados, sinIdentificar }.
 * Identificado = tiene invitación (lo matcheó el registrant); se cuenta una vez
 * por invitación aunque haya entrado desde dos dispositivos.
 */
export function participantesDe(vivo) {
  const crudos = vivo && vivo.participantes;
  const lista = Array.isArray(crudos)
    ? crudos.filter(Boolean)
    : Object.entries(crudos && typeof crudos === "object" ? crudos : {}).map(([clave, p]) => ({ clave, ...(p || {}) }));
  const porInvitacion = new Map();
  const sinIdentificar = [];
  lista.forEach((p, i) => {
    const fila = {
      clave: String(p.clave || p.invitacionId || p.registrantId || i),
      nombre: String(p.nombre || "Sin nombre"),
      unidades: unidadesDe(p),
      conectado: p.conectado === true,
    };
    if (p.invitacionId != null && p.invitacionId !== "") {
      const k = String(p.invitacionId);
      const prev = porInvitacion.get(k);
      porInvitacion.set(k, prev ? { ...prev, conectado: prev.conectado || fila.conectado } : { ...fila, clave: `inv-${k}` });
    } else {
      sinIdentificar.push(fila);
    }
  });
  const orden = (a, b) => (a.unidades || a.nombre).localeCompare(b.unidades || b.nombre, "es", { numeric: true });
  return { identificados: [...porInvitacion.values()].sort(orden), sinIdentificar: sinIdentificar.sort(orden) };
}

/** ¿La licencia avisa una superposición con otra reunión? (forma tolerante: el servidor la resume) */
function haySuperposicion(licencia) {
  if (!licencia || typeof licencia !== "object") return false;
  if (licencia.superpone === true || licencia.ok === false) return true;
  return Array.isArray(licencia.superposiciones) && licencia.superposiciones.length > 0;
}

/** "el 05/11, de 18:45 a 22:00" (Montevideo), o "" si faltan las horas. */
function franja(desdeMs, hastaMs) {
  if (!msValido(desdeMs) || !msValido(hastaMs)) return "";
  return `el ${diaMesMontevideo(desdeMs)}, de ${horaMontevideo(desdeMs)} a ${horaMontevideo(hastaMs)}`;
}

/**
 * Avisos de Zoom que devuelven guardar y convocar (no bloquean) → textos.
 *   avisoLicencia  { superposiciones: [{ tipo: "asamblea"|"zoom", desdeMs, hastaMs }], ... }:
 *                  la licencia admite una reunión a la vez y esta se pisa con otra
 *                  (la franja es la de la otra, con los márgenes de la sala).
 *   avisoCapacidad { capacidad, participantes }: más gente que la que entra en la sala.
 */
export function textosAvisosZoom(res) {
  const out = [];
  const lic = res && res.avisoLicencia;
  if (lic && typeof lic === "object") {
    const lista = Array.isArray(lic.superposiciones) ? lic.superposiciones.filter(Boolean) : [];
    if (lista.length) {
      const con = lista.map((s) => {
        const que = s.tipo === "zoom" ? "otra reunión de la cuenta de Zoom de Habitia" : "otra asamblea con Zoom";
        const cuando = franja(s.desdeMs, s.hastaMs);
        return cuando ? `${que} (${cuando})` : que;
      });
      out.push(
        `La licencia de Zoom admite una reunión a la vez y esta asamblea se superpone con ${con.join(" y con ")}. ` +
          "Cambiá el horario o usá un link manual: Habitia lo vuelve a revisar 24 horas antes."
      );
    } else if (haySuperposicion(lic)) {
      out.push("La licencia de Zoom admite una reunión a la vez y esta asamblea se superpone con otra. Cambiá el horario o usá un link manual.");
    }
  }
  const cap = res && res.avisoCapacidad;
  const capacidad = cap ? Number(cap.capacidad) : NaN;
  const participantes = cap ? Number(cap.participantes) : NaN;
  if (Number.isFinite(capacidad) && Number.isFinite(participantes) && participantes > capacidad) {
    out.push(
      `La sala de Zoom admite hasta ${capacidad} participantes y esta asamblea suma ${participantes} (propietarios, anfitrión y, en una mixta, la sala). ` +
        "Los que pasen del cupo no van a poder entrar: usá un link manual de una sala más grande o pedile a Habitia más capacidad."
    );
  }
  return out;
}

const visible = () => typeof document === "undefined" || document.visibilityState !== "hidden";

export default function AsambleaSalaSeccion({ asamblea, onCambio }) {
  const aid = asamblea.id;
  const { showToast } = useToast();
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState("");
  const [trabajando, setTrabajando] = useState(null); // "anfitrion" | "reintentar" | "manual" | null
  const [modalManual, setModalManual] = useState(false);
  const [link, setLink] = useState("");
  const [errorLink, setErrorLink] = useState("");
  const [ahora, setAhora] = useState(() => Date.now());
  const montado = useRef(true);
  const enVuelo = useRef(false);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const refrescar = useCallback(async () => {
    if (enVuelo.current) return;
    enVuelo.current = true;
    try {
      const res = await pedirSala(aid);
      if (!montado.current) return;
      setDatos(res || {});
      setErrorCarga("");
    } catch (e) {
      if (montado.current) setErrorCarga(mensajeDeError(e));
    } finally {
      enVuelo.current = false;
      if (montado.current) {
        setCargando(false);
        setAhora(Date.now());
      }
    }
  }, [aid]);

  useEffect(() => {
    refrescar();
  }, [refrescar]);

  const video = (datos && datos.video) || asamblea.video || {};
  const vivo = datos && datos.vivo ? datos.vivo : null;
  const enCurso = !!vivo && vivo.estado === "en_curso";
  // La ventana del servidor manda (la calcula con su reloj y sus márgenes); hasta
  // que llega, la misma cuenta en el cliente.
  const ventana = ventanaAnfitrion(asamblea, datos && datos.ventanaAnfitrion);
  const desdeMs = ventana ? ventana.desdeMs : null;
  const hastaMs = ventana ? ventana.hastaMs : null;
  const enVentanaAnfitrion = enVentana(ventana, ahora);

  // Polling de la vista en vivo: cada 15 s, solo en curso y con la pestaña visible.
  useEffect(() => {
    if (!enCurso) return undefined;
    let cancelado = false;
    let t = null;
    const programar = () => {
      clearTimeout(t);
      if (cancelado || !visible()) return;
      t = setTimeout(async () => {
        await refrescar();
        programar();
      }, POLL_MS);
    };
    const alCambiarVisibilidad = () => {
      if (!visible()) {
        clearTimeout(t);
        return;
      }
      refrescar().then(programar);
    };
    programar();
    document.addEventListener("visibilitychange", alCambiarVisibilidad);
    return () => {
      cancelado = true;
      clearTimeout(t);
      document.removeEventListener("visibilitychange", alCambiarVisibilidad);
    };
  }, [enCurso, refrescar]);

  // Fuera de "en curso": al volver a la pestaña dentro de la ventana del anfitrión
  // (típico después de abrir Zoom) se actualiza una vez. No hay polling.
  useEffect(() => {
    if (enCurso || !enVentanaAnfitrion) return undefined;
    const alVolver = () => {
      if (visible()) refrescar();
    };
    document.addEventListener("visibilitychange", alVolver);
    return () => document.removeEventListener("visibilitychange", alVolver);
  }, [enCurso, enVentanaAnfitrion, refrescar]);

  // El botón del anfitrión se habilita y se apaga solo en los bordes de la
  // ventana (si un borde ya pasó sin reflejarse, se pone al día enseguida).
  useEffect(() => {
    const limites = [desdeMs, hastaMs].filter((l) => Number.isFinite(l));
    const ya = Date.now();
    if (limites.some((l) => l > ahora && l <= ya)) {
      setAhora(ya);
      return undefined;
    }
    const espera = msHastaProximo(limites, ya);
    if (espera == null) return undefined;
    const t = setTimeout(() => setAhora(Date.now()), Math.min(espera + 50, MAX_TIMEOUT_MS));
    return () => clearTimeout(t);
  }, [desdeMs, hastaMs, ahora]);

  /**
   * Abre el acceso de anfitrión ("anfitrion": startUrl de iniciarAnfitrion) o
   * el de la computadora del salón en una mixta ("sala": joinUrl de
   * accesoSala, que nunca trae el start_url). Los dos se piden frescos y se
   * usan una sola vez para navegar la pestaña, que se abre en el click (antes
   * del await) para que el navegador no la bloquee. No quedan en el estado ni
   * en el storage.
   */
  const abrirAcceso = async (cual) => {
    const w = window.open("", "_blank");
    if (!w) {
      showToast("El navegador bloqueó la pestaña nueva: habilitá las ventanas emergentes para Habitia y probá de nuevo.", "warning");
      return;
    }
    try {
      w.opener = null;
    } catch (_) {
      /* algunos navegadores no lo dejan cambiar */
    }
    setTrabajando(cual);
    try {
      const res = cual === "sala" ? await accesoSala(aid) : await iniciarAnfitrion(aid);
      const url = urlNavegable(res && (cual === "sala" ? res.joinUrl : res.startUrl));
      if (!url) {
        throw new Error(
          cual === "sala"
            ? "La computadora de la sala todavía no tiene su acceso. Probá de nuevo en unos minutos."
            : "El servidor no devolvió el acceso de anfitrión."
        );
      }
      w.location.href = url;
    } catch (e) {
      try {
        w.close();
      } catch (_) {
        /* ya cerrada */
      }
      showToast(textoMotivoZoom(e, cual), "error");
    } finally {
      if (montado.current) setTrabajando(null);
    }
  };

  // Sin sala, reintentarSala la crea (gasta el cupo del edificio); con la sala
  // creada en error, destraba lo que Zoom rechazó (el cambio de horario lo
  // vuelve a probar el cron) y mira la licencia y los accesos en el momento.
  const reintentar = async () => {
    const conSala = !!(datos && esObjeto(datos.sala) && datos.sala.creada === true);
    setTrabajando("reintentar");
    try {
      const res = (await reintentarSala(aid)) || {};
      const v = esObjeto(res.video) ? res.video : {};
      if (v.estado === "error" && v.error === "superposicion") {
        showToast("Zoom sigue mostrando otra reunión en la misma franja: cambiá el horario o pasá a link manual.", "warning");
      } else if (conSala) {
        showToast("Listo: lo volvemos a probar en unos minutos.", "success");
      } else if (res.creada === true) {
        showToast("Listo: la sala de Zoom quedó creada.", "success");
      } else if (v.estado === "error") {
        showToast("Zoom no pudo crear la sala: probá de nuevo más tarde o pasá a link manual.", "error");
      } else {
        showToast("Listo: volvemos a intentar crear la sala en unos minutos.", "success");
      }
      await refrescar();
    } catch (e) {
      showToast(textoMotivoZoom(e, "reintentar"), detallesDeError(e).motivo === "limite_zoom_edificio" ? "warning" : "error");
    } finally {
      if (montado.current) setTrabajando(null);
    }
  };

  const abrirManual = () => {
    setLink("");
    setErrorLink("");
    setModalManual(true);
  };

  const confirmarManual = async () => {
    const l = link.trim();
    if (!/^https:\/\/\S{4,500}$/.test(l)) {
      setErrorLink("Pegá el link completo de la videollamada (empieza con https://).");
      return;
    }
    setTrabajando("manual");
    try {
      await pasarAManual(aid, l);
      setModalManual(false);
      showToast("La asamblea pasó a link manual: los propietarios lo ven 15 minutos antes, en la app y en el enlace de su citación.", "success");
      if (onCambio) await onCambio();
    } catch (e) {
      setErrorLink(mensajeDeError(e));
    } finally {
      if (montado.current) setTrabajando(null);
    }
  };

  // ── Render ──
  const titulo = (
    <h2 style={S.h2}>
      <FaVideo size={14} color={colors.primary} aria-hidden="true" /> Sala de Zoom
    </h2>
  );

  if (cargando && !datos) {
    return (
      <div style={cardBase}>
        {titulo}
        <p style={S.hint}>Cargando…</p>
      </div>
    );
  }

  const estSala = ESTADOS_SALA[video.estado] || { label: "Sin sala todavía", badge: "libre" };
  const conError = video.estado === "error";
  const infoSala = datos && esObjeto(datos.sala) ? datos.sala : {};
  // "sala_inexistente" ya viene con creada false (la reunión se borró en Zoom).
  const salaCreada = infoSala.creada === true;
  // Con la sala creada, un error (la licencia superpuesta, o el cambio de
  // horario o los accesos que Zoom rechazó o que siguen fallando) no la apaga:
  // el servidor deja iniciarla y abrir el acceso de la sala igual.
  const errorConSala = conError && salaCreada;
  const salaLista = video.estado === "lista" || enCurso || errorConSala;
  const terminada = video.estado === "finalizada" || video.estado === "borrada";
  const pendiente = video.estado === "pendiente" || video.estado === "creando" || !video.estado;
  // Sin cupo diario del edificio, el cron la crea cuando se libera (00:00 UTC).
  const sinCupoEdificio = video.estado === "pendiente" && infoSala.ultimoError === "limite_zoom_edificio";
  const mixta = asamblea.modalidad === "mixta";
  const cap = (datos && datos.capacidad) || null;
  const capacidadMaxima = cap ? Number(cap.maxima) : NaN;
  // Propietarios + anfitrión (+ la computadora del salón en una mixta), como al convocar.
  const participantesPrevistos = cap ? Number(cap.propietarios) + 1 + (mixta ? 1 : 0) : NaN;
  const excedeCapacidad =
    Number.isFinite(capacidadMaxima) && capacidadMaxima > 0 && Number.isFinite(participantesPrevistos) && participantesPrevistos > capacidadMaxima;
  const reg = (datos && datos.registrants) || {};
  const activos = cuenta(reg.activos);
  const cancelados = cuenta(reg.cancelados);
  const { identificados, sinIdentificar } = participantesDe(vivo);
  const conectados = identificados.filter((p) => p.conectado).length;
  const total = activos || identificados.length;
  const antesDeVentana = desdeMs != null && ahora < desdeMs;
  const despuesDeVentana = hastaMs != null && ahora >= hastaMs;
  // Mixta: la computadora del salón no hace esperar el cierre. Si de la reunión
  // en curso quedó sola (sin el anfitrión ni propietarios identificados), al
  // cierre de la sala se termina aunque la asamblea siga: lo avisa el servidor
  // desde 15 minutos antes (sala.avisoCierre = {motivo: "solo_sala", cierreMs},
  // con su reloj y sus participantes).
  const avisoCierre = esObjeto(infoSala.avisoCierre) && infoSala.avisoCierre.motivo === "solo_sala" ? infoSala.avisoCierre : null;
  const cierreAvisoMs = avisoCierre && msValido(Number(avisoCierre.cierreMs)) ? Number(avisoCierre.cierreMs) : hastaMs;
  const estVivo = vivo ? ESTADOS_VIVO[vivo.estado] || null : null;
  const estGrab = vivo ? GRABACION[vivo.grabacion] || null : null;

  const columnas = [
    { key: "unidades", label: "Unidad", render: (u) => u || "—" },
    { key: "nombre", label: "Propietario" },
    {
      key: "conectado",
      label: "Estado",
      render: (c) => <StatusBadge status={c ? "presente" : "cerrada"} label={c ? "Conectado" : "Se fue"} size="sm" />,
    },
  ];

  return (
    <div style={{ ...cardBase, display: "flex", flexDirection: "column", gap: spacing[4] }}>
      <div style={S.head}>
        {titulo}
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2] }}>
          <StatusBadge status={estSala.badge} label={estSala.label} size="sm" />
          <button
            type="button"
            style={{ ...btnSecondary, ...S.botonChico }}
            className="habitia-btn-secondary"
            onClick={refrescar}
            aria-label="Actualizar el estado de la sala"
            title="Actualizar"
          >
            <FaSyncAlt size={12} />
          </button>
        </div>
      </div>

      {errorCarga && (
        <p style={{ ...S.texto, color: colors.errorDark }} role="alert">
          No pudimos cargar el estado de la sala: {errorCarga}
        </p>
      )}

      {pendiente && !sinCupoEdificio && <p style={S.texto}>La sala se crea sola en unos minutos; no hace falta que hagas nada.</p>}

      {sinCupoEdificio && (
        <div style={S.avisoConAcciones} role="status">
          <span>{textoTopeEdificio("crear", ahora)}</span>
          <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={abrirManual} disabled={!!trabajando}>
              <FaLink /> Pasar a link manual
            </button>
          </div>
        </div>
      )}

      {conError && (
        <div style={S.error} role="alert">
          <span>{textoErrorSala(video.error, infoSala)}</span>
          <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={reintentar} disabled={!!trabajando}>
              <FaRedo /> {trabajando === "reintentar" ? "Reintentando…" : "Reintentar"}
            </button>
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={abrirManual} disabled={!!trabajando}>
              <FaLink /> Pasar a link manual
            </button>
          </div>
        </div>
      )}

      {haySuperposicion(datos && datos.licencia) && !conError && (
        <div style={S.aviso} role="status">
          La cuenta de Zoom tiene otra reunión que se superpone con esta asamblea (la licencia admite una por vez). Revisá el
          horario.
        </div>
      )}

      {excedeCapacidad && !terminada && (
        <div style={S.aviso} role="status">
          La sala de Zoom admite hasta {capacidadMaxima} participantes y esta asamblea suma {participantesPrevistos} (propietarios,
          anfitrión{mixta ? " y la sala" : ""}). Los que pasen del cupo no van a poder entrar.
        </div>
      )}

      {(activos > 0 || cancelados > 0) && (
        <p style={S.hint}>
          Accesos personales: {activos} {activos === 1 ? "activo" : "activos"}
          {cancelados > 0 ? ` · ${cancelados} ${cancelados === 1 ? "cancelado" : "cancelados"}` : ""}. Cada propietario entra con
          el suyo desde la app o el enlace que le llega 15 minutos antes.
        </p>
      )}

      {avisoCierre && (
        <div style={S.aviso} role="alert">
          Solo queda conectada la computadora del salón: la reunión de Zoom se termina
          {msValido(cierreAvisoMs) ? ` a las ${horaMontevideo(cierreAvisoMs)},` : ""} al cierre de la sala. Si la asamblea sigue,
          iniciá como anfitrión o extendé la duración desde “Reprogramar o corregir”.
        </div>
      )}

      {/* Anfitrión (y, en una mixta, la computadora del salón) */}
      {!terminada && (!conError || errorConSala) && (
        <div style={S.anfitrion}>
          <div style={{ display: "flex", flexDirection: "column", gap: spacing[1], flex: 1, minWidth: 220 }}>
            <span style={S.texto}>Anfitrión</span>
            <span style={S.hint}>
              {!salaLista
                ? "Se puede iniciar cuando la sala esté lista."
                : antesDeVentana
                  ? `Se habilita una hora antes, a las ${horaMontevideo(desdeMs)}.`
                  : despuesDeVentana
                    ? "La ventana para iniciar como anfitrión ya terminó."
                    : "Abrila en un solo dispositivo: si la abrís en otro, Zoom saca al primero."}
            </span>
            {salaLista && !despuesDeVentana && (
              <span style={S.hint}>
                El acceso de anfitrión es de la cuenta de Zoom de Habitia y queda abierto en el navegador donde lo uses: abrilo
                desde una ventana privada (Habitia en incógnito) y cerrala al terminar.
              </span>
            )}
            {mixta && salaLista && !despuesDeVentana && (
              <span style={S.hint}>
                “Abrir el acceso de la sala” es para la computadora del salón: entra como “Sala” y no cuenta para el quórum.
                Abrilo en esa computadora.
              </span>
            )}
            {mixta && salaLista && !despuesDeVentana && msValido(hastaMs) && (
              <span style={S.hint}>
                Mantené el anfitrión conectado hasta el final: si en la reunión queda solo la computadora del salón, se termina a
                las {horaMontevideo(hastaMs)}, al cierre de la sala, aunque la asamblea siga en el salón.
              </span>
            )}
          </div>
          <div style={{ display: "flex", gap: spacing[2], flexWrap: "wrap" }}>
            {mixta && (
              <button
                type="button"
                style={btnSecondary}
                className="habitia-btn-secondary"
                onClick={() => abrirAcceso("sala")}
                disabled={!salaLista || !enVentanaAnfitrion || !!trabajando}
              >
                <FaDesktop /> {trabajando === "sala" ? "Abriendo…" : "Abrir el acceso de la sala"}
              </button>
            )}
            <button
              type="button"
              style={btnPrimary}
              className="habitia-btn-primary"
              onClick={() => abrirAcceso("anfitrion")}
              disabled={!salaLista || !enVentanaAnfitrion || !!trabajando}
            >
              <FaPlay /> {trabajando === "anfitrion" ? "Abriendo…" : "Iniciar como anfitrión"}
            </button>
          </div>
        </div>
      )}

      {/* En vivo */}
      {vivo && estVivo && (
        <div style={{ display: "flex", flexDirection: "column", gap: spacing[3] }} aria-label="Asamblea en vivo">
          <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flexWrap: "wrap" }}>
            <StatusBadge status={estVivo.badge} label={estVivo.label} size="sm" />
            {estGrab && <StatusBadge status={estGrab.badge} label={estGrab.label} size="sm" />}
            {enCurso && <span style={S.hint}>Se actualiza cada 15 segundos mientras tengas esta pestaña abierta.</span>}
          </div>
          {vivo.estado !== "esperando" && (
            <>
              <div style={S.kpis}>
                <KPICard icon={FaUsers} label="Propietarios conectados" value={`${conectados} de ${total}`} />
                <KPICard
                  icon={FaUserSlash}
                  iconBg={sinIdentificar.length ? colors.warningLight : colors.gray100}
                  iconColor={sinIdentificar.length ? colors.warning : colors.gray600}
                  label="Sin identificar"
                  value={sinIdentificar.length}
                />
              </div>
              {Number(vivo.anfitrionSimultaneas) >= 2 && (
                <div style={S.aviso} role="status">
                  La cuenta anfitriona de Habitia se usó desde {Number(vivo.anfitrionSimultaneas)} dispositivos a la vez. Esas
                  conexiones no cuentan como propietarios: si alguno entró con esa cuenta (por ejemplo, porque tenía la app de Zoom
                  abierta con ella), marcá su asistencia a mano en el acta.
                </div>
              )}
              <DataTable
                columns={columnas}
                data={identificados}
                emptyIcon={FaUsers}
                emptyTitle="Todavía no entró ningún propietario"
                emptyDescription="Cuando entren con su acceso personal, los vas a ver acá por unidad."
              />
              {sinIdentificar.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: spacing[2] }}>
                  <span style={S.texto}>Sin identificar</span>
                  <span style={S.hint}>
                    Entraron sin su acceso personal (o con otro nombre). En el acta quedan marcados para que los resuelvas.
                  </span>
                  <ul style={S.lista} aria-label="Participantes sin identificar">
                    {sinIdentificar.map((p) => (
                      <li key={p.clave} style={S.filaLista}>
                        <span style={{ ...S.texto, flex: 1 }}>{p.nombre}</span>
                        <StatusBadge status={p.conectado ? "presente" : "cerrada"} label={p.conectado ? "Conectado" : "Se fue"} size="sm" />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <AdminModal
        open={modalManual}
        onClose={() => setModalManual(false)}
        title="Pasar a link manual"
        size="narrow"
        footer={
          <div style={{ display: "flex", gap: spacing[2], justifyContent: "flex-end", width: "100%" }}>
            <button type="button" style={btnSecondary} className="habitia-btn-secondary" onClick={() => setModalManual(false)}>
              Volver
            </button>
            <button type="button" style={btnPrimary} className="habitia-btn-primary" onClick={confirmarManual} disabled={trabajando === "manual"}>
              <FaLink /> {trabajando === "manual" ? "Guardando…" : "Usar este link"}
            </button>
          </div>
        }
      >
        <p style={{ ...S.texto, marginTop: 0 }}>
          La asamblea deja de usar la sala de Zoom de Habitia. Pegá el link de una videollamada que ya creaste: los propietarios
          lo ven 15 minutos antes, en la app y en el enlace de su citación.
        </p>
        <FormField label="Link de la videollamada" htmlFor={`asm-sala-link-${aid}`} required error={errorLink}>
          <input
            id={`asm-sala-link-${aid}`}
            type="url"
            className="habitia-input"
            style={inputBase}
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://…"
          />
        </FormField>
      </AdminModal>
    </div>
  );
}

const S = {
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: spacing[3], flexWrap: "wrap" },
  h2: { margin: 0, fontSize: typography.sizes.lg, color: colors.black, fontWeight: 400, display: "flex", alignItems: "center", gap: spacing[2] },
  texto: { margin: 0, fontSize: typography.sizes.md, color: colors.gray900, lineHeight: 1.5 },
  hint: { margin: 0, fontSize: typography.sizes.sm, color: colors.gray600, lineHeight: 1.5 },
  botonChico: { padding: "8px 12px", fontSize: typography.sizes.base },
  error: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
    background: colors.errorLight,
    color: colors.errorDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
  },
  aviso: {
    background: colors.warningLight,
    color: colors.warningDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
  },
  avisoConAcciones: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[3],
    background: colors.warningLight,
    color: colors.warningDark,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    fontSize: typography.sizes.md,
  },
  anfitrion: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing[3],
    flexWrap: "wrap",
    background: colors.gray50,
    borderRadius: radius.sm,
    padding: `${spacing[3]}px ${spacing[4]}px`,
  },
  kpis: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: spacing[4] },
  lista: { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: spacing[2] },
  filaLista: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    background: colors.gray50,
    borderRadius: radius.sm,
    padding: `${spacing[2]}px ${spacing[3]}px`,
  },
};
